import { Instagram, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * /marketing/instagram/ — Canal Instagram.
 *
 * Placeholder hasta integrar la Instagram Graph API. Requiere:
 *  - App de Meta para desarrolladores con la cuenta de Instagram Business
 *    asociada a la página de Facebook de Duetto Catering.
 *  - Access token de larga duración (60 días, autorenovable).
 *  - Endpoint backend que cachee los datos (la API tiene límites de cuota
 *    estrictos: 200 llamadas por hora por usuario).
 */
export default function MarketingInstagram() {
  const requisitos = [
    { hecho: false, txt: 'Cuenta de Instagram en modo "Empresa" o "Creador" (no "Personal").' },
    { hecho: false, txt: 'Página de Facebook de Duetto enlazada con la cuenta de Instagram.' },
    { hecho: false, txt: 'App en developers.facebook.com con permisos instagram_basic + instagram_manage_insights + pages_show_list + pages_read_engagement.' },
    { hecho: false, txt: 'Long-lived access token guardado en backend (.env).' },
    { hecho: false, txt: 'IG_BUSINESS_ACCOUNT_ID identificado.' },
    { hecho: false, txt: 'Endpoint /api/marketing/instagram/* en backend con cache (sync diario).' },
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Instagram className="w-7 h-7 text-pink-600" />
        <h1 className="text-2xl font-bold text-duetto-primary">Instagram</h1>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
          Próximamente
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Insights del perfil de Instagram de Duetto Catering: seguidores, alcance, engagement
        por post y rendimiento por tipo de contenido (reels vs. carruseles vs. estáticas).
      </p>

      {/* Aviso */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          La integración con Instagram requiere configuración externa (app de Meta + tokens).
          Sin ese setup, esta página seguirá vacía. Marca a Claude cuando tengas el token
          y las credenciales para arrancar la integración.
        </div>
      </div>

      {/* Requisitos */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-3">Qué hace falta para activarlo</h2>
        <ul className="space-y-2 text-sm text-gray-700">
          {requisitos.map((r, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${r.hecho ? 'text-green-600' : 'text-gray-300'}`} />
              <span>{r.txt}</span>
            </li>
          ))}
        </ul>
        <a
          href="https://developers.facebook.com/docs/instagram-platform/instagram-graph-api"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-4 text-sm text-duetto-primary hover:underline"
        >
          <ExternalLink className="w-4 h-4" />
          Documentación oficial Instagram Graph API
        </a>
      </div>

      {/* Qué llegará */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="font-semibold text-gray-900 mb-3">Qué llegará a esta página</h2>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>· KPIs del perfil (seguidores, alcance 28 días, impresiones, engagement).</li>
          <li>· Lista de publicaciones recientes con sus métricas (likes, comentarios, saves, shares).</li>
          <li>· Comparativa por formato (reel / carrusel / estática) , qué funciona mejor.</li>
          <li>· Mejores horas de publicación según engagement histórico.</li>
          <li>· Crecimiento de seguidores (gráfica) y picos correlacionados con publicaciones.</li>
        </ul>
      </div>

      <div className="mt-6 text-center">
        <a
          href="https://www.instagram.com/duettocatering/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-duetto-primary"
        >
          <ExternalLink className="w-4 h-4" />
          Ir al perfil público @duettocatering
        </a>
      </div>
    </div>
  );
}
